/**
 * Display formatting.
 *
 * Money goes through `@bingo/shared`, for the reason given at length in the Mini
 * App's copy of this file: a second implementation is how "500 santim" becomes
 * "5.00" on one screen and "500.00" on another. That argument is stronger here,
 * not weaker — this console is where someone decides whether the number on a
 * screenshot matches the number in the claim.
 */

import { santim, formatEtb, parseEtb } from '@bingo/shared';

export function etb(amount: string | null | undefined): string {
  if (amount === null || amount === undefined) return '—';
  try {
    return `ETB ${formatEtb(santim(amount))}`;
  } catch {
    return '—';
  }
}

export type AmountParse = { ok: true; santim: string } | { ok: false };

export function parseAmount(input: string): AmountParse {
  const cleaned = input.replace(/[\s,]/g, '');
  if (cleaned === '') return { ok: false };
  try {
    return { ok: true, santim: parseEtb(cleaned).toString() };
  } catch {
    return { ok: false };
  }
}

/**
 * A timestamp as the operator's local time, to the minute.
 *
 * Local rather than UTC because the people reading this are in one place and
 * are comparing it against a bank app on their phone, which shows local time.
 * The absolute value is in the audit trail for anyone who needs to be exact.
 */
export function at(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** "12 minutes ago" — how long a claim has been waiting. */
export function since(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return '—';
  const seconds = Math.floor((now - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

/**
 * A uuid shortened for reading aloud.
 *
 * Never used to *identify* anything in a request — only to label one on screen.
 * Eight hex characters is far too few to be unique across a real table, and a
 * screen that let someone act on a truncated id would eventually act on the
 * wrong row.
 */
export function shortId(id: string | null | undefined): string {
  return id ? id.slice(0, 8) : '—';
}
